"use client";

import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FaLink, FaLinkedinIn } from "react-icons/fa";
import { BsWhatsapp } from "react-icons/bs";
import { FiMail } from "react-icons/fi";
import { IconType } from "react-icons";
import { IoMdShare } from "react-icons/io";
import { usePathname } from "next/navigation";

import envConfig from "@/config/envConfig";
import { socialShareOptions } from "@/data/social";
import { useExclusiveDropdown, usePortalDropdown } from "@/hooks";
import { useMessage } from "../providers/MessageProvider";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";

const dropdownIconMap: Record<string, { icon: IconType; bg: string }> = {
  Whatsapp: { icon: BsWhatsapp, bg: "#25D366" },
  LinkedIn: { icon: FaLinkedinIn, bg: "#0A66C2" },
  Email: { icon: FiMail, bg: "#6B7280" },
};

interface SocialShareProps {
  title: string;
  url: string;
  customButton?: ReactNode;
  /** "up" opens dropdown above button (for cards at bottom of container) */
  dropdownPosition?: "up" | "down";
  /** Use portal to render dropdown in body (avoids overflow clipping) */
  usePortal?: boolean;
}

const getLocalizedSocialName = (name: string, language: string): string => {
  if (language !== "te") return name;

  const socialNames: Record<string, string> = {
    Facebook: "ఫేస్బుక్",
    Instagram: "ఇన్స్టాగ్రామ్",
    YouTube: "యూట్యూబ్",
    X: "X",
    Whatsapp: "వాట్సాప్",
    LinkedIn: "లింక్డ్ఇన్",
    Email: "ఇమెయిల్",
  };

  return socialNames[name] || name;
};

export default function SocialShare({
  title,
  url,
  customButton,
  dropdownPosition = "down",
  usePortal = true,
}: SocialShareProps) {
  const { isOpen: isDropdownOpen, toggleDropdown, closeDropdown } = useExclusiveDropdown();
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const language = locale?.split("-")[0] || "en";

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { showMessage } = useMessage();

  const { dropdownStyle, isPositionReady } = usePortalDropdown({
    isOpen: isDropdownOpen,
    usePortal,
    triggerRef,
    dropdownPosition,
    estimatedHeight: 320,
    onClose: closeDropdown,
  });

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      closeDropdown();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen, closeDropdown]);

  let baseUrl = (envConfig?.clientUrl || "").replace(/\/+$/, "");
  if (!baseUrl && typeof window !== "undefined" && !window.location.hostname.includes("backenddev")) {
    baseUrl = window.location.origin;
  }
  const path = (url || "").startsWith("/") ? url : `/${url || ""}`;
  const sharePath = path.replace(/^\/(te-IN|en-IN)(?=\/|$)/i, "") || "/";
  const fullPageUrl = baseUrl ? `${baseUrl}${sharePath}` : sharePath;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(fullPageUrl);
    showMessage(
      "info",
      language === "te" ? "లింక్ క్లిప్‌బోర్డ్‌కు కాపీ చేయబడింది!" : "Link copied to clipboard!",
    );
    closeDropdown();
  };

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={`white shadow-md z-[9999] bg-white dark:bg-[#444] dark:text-white min-w-[180px] py-3 rounded ${
        !usePortal
          ? dropdownPosition === "up"
            ? "absolute right-0 bottom-[100%] mb-2"
            : "absolute right-0 top-[100%] mt-1"
          : ""
      }`}
      style={usePortal ? dropdownStyle : undefined}
    >
      <div
        className="flex items-center gap-2 cursor-pointer px-4 py-2 transition-colors hover:text-[#0055a5]"
        onClick={handleCopyLink}
      >
        <span className="flex items-center justify-center">
          <FaLink />
        </span>
        <span className="text-sm font-medium whitespace-nowrap">
          {language === "te" ? "లింక్ కాపీ చేయండి" : "Copy Link"}
        </span>
      </div>
      {socialShareOptions?.map((item, index) => {
        const mapped = dropdownIconMap[item.name];
        const Icon = mapped?.icon ?? item.icon;
        const shareLink = item.generateLink(fullPageUrl, title ?? "");
        const localizedName = getLocalizedSocialName(item.name, language);

        return (
          <a
            key={index}
            href={shareLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on ${item.name}`}
            className="flex items-center gap-2 px-4 py-2 transition-colors hover:text-[#0055a5]"
            onClick={closeDropdown}
          >
            <span className="flex items-center justify-center w-5 h-5 shrink-0">
              <Icon className="w-5 h-5" />
            </span>
            <span className="text-sm font-medium whitespace-nowrap">{localizedName}</span>
          </a>
        );
      })}
    </div>
  );

  const shouldRenderDropdown = isDropdownOpen && (!usePortal || isPositionReady);

  return (
    <div className="relative inline-flex items-center justify-center">
      <div
        ref={triggerRef}
        onClick={toggleDropdown}
        className="inline-flex size-6 shrink-0 items-center justify-center"
      >
        {customButton ? (
          customButton
        ) : (
          <button
            type="button"
            className="inline-flex size-6 items-center justify-center text-[#585858] dark:text-white"
            aria-label="Share"
          >
            <IoMdShare size={16} />
          </button>
        )}
      </div>
      {shouldRenderDropdown &&
        (usePortal && typeof document !== "undefined"
          ? createPortal(dropdownContent, document.body)
          : dropdownContent)}
    </div>
  );
}
